import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { telemetry } from '../lib/telemetry'

interface Props {
  children: ReactNode
  fallbackTitle?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    telemetry.reportReactError({
      message: error.message,
      componentStack: errorInfo.componentStack ?? undefined,
    })
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      return (
        <div className="min-h-[40vh] flex items-center justify-center p-8">
          <div className="card max-w-md w-full py-12 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-800 mb-2">
              {this.props.fallbackTitle ?? 'Algo salió mal'}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {this.state.error.message || 'Ha ocurrido un error inesperado.'}
            </p>
            <button
              type="button"
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm font-medium"
            >
              <RotateCcw className="w-4 h-4" />
              Reintentar
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
